// =========================================================================
// HelpyDoc Metabolic · App Shell principal (TypeScript)
// =========================================================================
import { useState, useEffect, useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ICONOS_CATEGORIA, ICONOS_NAVEGACION } from '@/config/iconografia';
import { AuthService } from '@/services/AuthService';
import { tieneConsentimientosRequeridos, consentSignatureEnabled, hasValidConsentSignature, DataService } from '@/services';
import { necesitaOnboarding } from '@/lib/onboarding-estado';
import { reintentarSincronizacionValoracion } from '@/features/progreso/sincronizacion-valoracion';
import { waitForFirebaseReady } from '@/services/firebase';
import { ErrorBoundary, ToastContainer, useToast, ConsentSignature, AvisoSoloVisualizacion } from '@/components';
import { AuthScreens } from '@/screens/AuthScreens';
import { MobileScreens } from '@/screens/MobileScreens';
import type { Session } from '@/services/AuthService';
import { applyTheme, resolveTheme } from '@/utils/theme';

type Stage = 'splash' | 'login' | 'register' | 'onboarding' | 'consents' | 'sign-consent' | 'app';

// Barra inferior — orden y iconos de la lámina de marca (ver
// `config/iconografia.ts`). El botón central usa la misma ficha cuadrada que
// las acciones rápidas del escritorio: mismo degradado y mismo icono.
const NAV: { id: string; Icon: LucideIcon; label: string; center?: boolean }[] = [
  { id: 'home',      Icon: ICONOS_CATEGORIA.inicio,     label: 'Inicio'    },
  { id: 'actividad', Icon: ICONOS_NAVEGACION.actividad, label: 'Actividad' },
  { id: 'registro',  Icon: ICONOS_NAVEGACION.registrar, label: 'Registrar', center: true },
  { id: 'informes',  Icon: ICONOS_NAVEGACION.informes,  label: 'Informes'  },
  { id: 'perfil',    Icon: ICONOS_NAVEGACION.perfil,    label: 'Perfil'    },
];

// ── Shell autenticada ─────────────────────────────────────────────────────

interface AppAutenticadaProps {
  session: Session;
  onLogout: () => void;
  darkMode: boolean;
  onToggleDark: () => void;
}

// Pantallas de nivel superior (nav bar) — las demás son secundarias
const TOP_SCREENS = new Set(['home', 'actividad', 'registro', 'informes', 'perfil']);

/**
 * Pantalla de arranque pedida por el acceso directo del icono (`?ir=registro`).
 * Solo se aceptan destinos de una lista blanca: el parámetro viene de la URL y
 * no debe poder colar una pantalla arbitraria.
 *
 * La función es PURA a propósito (se usa como inicializador de estado): la
 * limpieza de la barra de direcciones va en un efecto. Hacer el
 * `replaceState` aquí rompía el atajo, porque StrictMode invoca dos veces el
 * inicializador y la segunda pasada ya no encontraba el parámetro.
 */
const ATAJOS_VALIDOS = new Set([
  'registro', 'diario', 'actividad', 'emocional', 'informes',
]);

function pantallaInicial(): string {
  try {
    // Solo `?ir=…`. La lectura de `pathname` existía para reconocer /studio y
    // /video-faktory, que abrían el panel del motor; ese panel vive desde el
    // 3-sep-2026 en videofaktory.helpydoc.com, con su repo y su dominio.
    const destino = new URLSearchParams(window.location.search).get('ir');
    if (destino && ATAJOS_VALIDOS.has(destino)) {
      return destino;
    }
  } catch { /* URL no parseable: se arranca en el inicio */ }
  return 'home';
}

function AppAutenticada({ session, onLogout, darkMode, onToggleDark }: AppAutenticadaProps) {
  const [screen, setScreen] = useState(pantallaInicial);
  const [navDir, setNavDir] = useState<'forward' | 'back'>('forward');
  const [refreshKey, setRefreshKey] = useState(0);
  const { toasts, show: showToast } = useToast();

  const goTo = useCallback((id: string) => {
    if (id === 'logout') { onLogout(); return; }
    const target = id === 'dashboard' ? 'home' : id;
    // Si volvemos a una pantalla top desde una secundaria → animación de retroceso
    const isBack = TOP_SCREENS.has(target) && !TOP_SCREENS.has(screen);
    setNavDir(isBack ? 'back' : 'forward');
    setScreen(target);
  }, [onLogout, screen]);

  // Quita `?ir=…` de la barra de direcciones una vez consumido, para que al
  // recargar no se vuelva siempre a la pantalla del atajo.
  useEffect(() => {
    if (window.location.search.includes('ir=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  return (
    <div className="mobile-shell">
      <ToastContainer toasts={toasts} />
      <AvisoSoloVisualizacion />

      {/* Aquí había una barra de estado SIMULADA, heredada del mockup web. En un
          móvil real no aportaba nada: la hora ya la da el sistema (y encima se
          superponía con la suya), y la fecha ya está bajo el saludo en Inicio.
          Al quitarla se recuperan 44 px de pantalla. El respeto a la isla
          dinámica no se pierde: lo da `padding-top: env(safe-area-inset-top)`
          de `.mobile-shell`, no esta barra. */}
      <div className="screen-scroll">
        <MobileScreens
          screen={screen}
          navDir={navDir}
          onNav={goTo}
          onLogout={onLogout}
          darkMode={darkMode}
          onToggleDark={onToggleDark}
          refreshKey={refreshKey}
          onRefresh={handleRefresh}
          showToast={showToast}
          session={session}
        />
      </div>

      <nav className="bottom-nav">
        {NAV.map(item => {
          if (item.center) {
            return (
              <button key={item.id} className="nav-center-btn" onClick={() => goTo(item.id)}>
                <div className="nav-center-circle">
                  {/* Mismo icono que la ficha cuadrada del escritorio. */}
                  <item.Icon size={26} strokeWidth={2.2} aria-hidden="true" />
                </div>
                <span className="nav-center-label">{item.label}</span>
              </button>
            );
          }
          const isActive = screen === item.id || (item.id === 'home' && ['emocional', 'platos', 'nevera'].includes(screen));
          return (
            <button
              key={item.id}
              className={`nav-item${isActive ? ' active' : ''}`}
              onClick={() => goTo(item.id)}
            >
              <item.Icon size={22} strokeWidth={2} aria-hidden="true" />
              <span className="nav-item-label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────

export default function App() {
  const [stage, setStage] = useState<Stage>('splash');
  const [session, setSession] = useState<Session | null>(null);
  const [darkMode, setDarkMode] = useState(() => resolveTheme() === 'dark');

  // Aplicar tema al montar y sincronizar con el sistema
  useEffect(() => {
    applyTheme(resolveTheme());
    setDarkMode(resolveTheme() === 'dark');
  }, []);

  // Propagar cambios de dark mode al DOM vía theme.ts
  useEffect(() => {
    applyTheme(darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // A dónde ir tras conceder el consentimiento (se calcula al autenticar).
  const [afterConsentStage, setAfterConsentStage] = useState<Stage>('app');

  // Tras autenticar, comprueba el consentimiento: si falta, intercala la
  // pantalla de consentimiento antes del destino (onboarding/app).
  //
  // HD-040: con la firma electrónica activa, el gate comprueba UNA vez
  // `hasValidConsentSignature()` (GET /consents/signature/current). Si hay firma
  // válida de la versión legal vigente → entra directo (NO se re-pide en cada
  // login). Solo se intercala la firma si falta o cambió la versión. Mientras el
  // backend HD-040 no esté desplegado en prod, `consentSignatureEnabled()` es
  // false en modo api → se mantiene el gate legacy (no rompe el login).
  const proceedWithConsentGate = useCallback(async (sess: Session, target: Stage) => {
    setSession(sess);
    if (consentSignatureEnabled()) {
      const signed = await hasValidConsentSignature();
      if (signed) {
        setStage(target);
      } else {
        setAfterConsentStage(target);
        setStage('sign-consent');
      }
      return;
    }
    // Gate legacy (pre-HD-040): consentimientos RGPD por checkbox.
    const ok = await tieneConsentimientosRequeridos();
    if (ok) {
      setStage(target);
    } else {
      setAfterConsentStage(target);
      setStage('consents');
    }
  }, []);

  // F08 G2 · Reanudación del onboarding. Un usuario ya autenticado (login o
  // auto-login) que nunca terminó el wizard debe volver a él, no colarse en
  // `app`. La señal vive en el PERFIL (`onboardingCompletedAt`), no en la
  // sesión, así que se lee de forma asíncrona. Fail-safe: si el perfil no se
  // puede leer (backend caído en modo api), se va a `app` — un fallo transitorio
  // nunca debe atrapar al usuario en onboarding ni romper el login P0.
  const resolverDestinoTrasAuth = useCallback(async (): Promise<Stage> => {
    // ADR-020 · reintento de la valoración inicial que quedó sellada en local
    // pero sin confirmar en el servidor (p. ej. la red falló al terminar el
    // onboarding). No bloqueante a propósito: nunca debe retrasar el login.
    void reintentarSincronizacionValoracion();
    try {
      const usuario = await DataService.getUsuario();
      return necesitaOnboarding(usuario) ? 'onboarding' : 'app';
    } catch {
      return 'app';
    }
  }, []);

  const handleSplashDone = () => {
    // Esperar a que Firebase Auth hidrate _fbUser antes de llamar a la API de consentimientos.
    // Sin esto, getIdToken() no tiene usuario Firebase → 401 → consent screen en cada login.
    void waitForFirebaseReady().then(async () => {
      const result = AuthService.autoLogin();
      if (result.ok && result.session) {
        const destino = await resolverDestinoTrasAuth();
        void proceedWithConsentGate(result.session, destino);
      } else setStage('login');
    });
  };

  const handleLogin = (sess: Session) => {
    void resolverDestinoTrasAuth().then(destino => proceedWithConsentGate(sess, destino));
  };

  const handleRegister = (sess: Session) => {
    void proceedWithConsentGate(sess, sess.isNew ? 'onboarding' : 'app');
  };

  const handleLogout = () => {
    AuthService.logout();
    setSession(null);
    setStage('login');
  };

  // Auth stages — fill #root directamente (en desktop = 926px, en móvil = 100dvh)
  if (stage !== 'app') {
    return (
      <div style={{ height: '100%', overflow: 'hidden' }}>
        {stage === 'splash'     && <AuthScreens type="splash" onDone={handleSplashDone} />}
        {stage === 'login'      && <AuthScreens type="login" onLogin={handleLogin} onGoRegister={() => setStage('register')} />}
        {stage === 'register'   && <AuthScreens type="register" onRegister={handleRegister} onGoLogin={() => setStage('login')} />}
        {stage === 'onboarding' && session && <AuthScreens type="onboarding" session={session} onDone={() => setStage('app')} />}
        {stage === 'consents'   && session && <AuthScreens type="consents" onDone={() => setStage(afterConsentStage)} onLogout={handleLogout} />}
        {stage === 'sign-consent' && session && (
          <ConsentSignature
            email={session.email}
            onDone={() => setStage(afterConsentStage)}
            onCancel={handleLogout}
          />
        )}
      </div>
    );
  }
  if (stage === 'app' && session) {
    return (
      <ErrorBoundary>
        <AppAutenticada
          session={session}
          onLogout={handleLogout}
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(d => !d)}
        />
      </ErrorBoundary>
    );
  }
  return null;
}
